import React, { useCallback, useContext, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  ProgressBar,
  Row,
  Spinner,
} from "react-bootstrap";
import { ArrowLeft, Download, RefreshCw } from "react-feather";
import DatePicker from "react-datepicker";
import Popconfirm from "../../../components/Popconfirm";
import MessageFields from "./MessageFields";
import { notyf } from "../../../contexts/NotyfContext";
import { PresetMessagesContext } from "../../../contexts/PresetMessagesContext";
import { downloadFile } from "../../../utils";
import { formatDay, percentOf } from "./index";
import {
  addReminder,
  closeCycle,
  exportCycle,
  getCycle,
  sendBatch,
  sendReminderBatch,
  updateSettings,
} from "../../../services/recertification";

const Stat = ({ label, value, hint, variant }) => (
  <Col md={3} sm={6} className="mb-3">
    <Card className="mb-0 h-100">
      <Card.Body className="py-3">
        <h3 className={`mb-1 ${variant ? `text-${variant}` : ""}`}>{value}</h3>
        <div className="text-muted small">{label}</div>
        {hint && <div className="text-muted small fst-italic">{hint}</div>}
      </Card.Body>
    </Card>
  </Col>
);

/**
 * Queued vs sent, side by side. Queued means we handed the families over; sent means the messages
 * actually left, which is what staff are really waiting on.
 */
const SendProgress = ({ queued, sent, total }) => (
  <ProgressBar className="mb-2" style={{ height: "10px" }}>
    <ProgressBar variant="success" now={percentOf(sent, total)} key="sent" />
    <ProgressBar variant="info" now={percentOf(queued - sent, total)} key="queued" />
  </ProgressBar>
);

function RecertificationDetail() {
  const { id } = useParams();
  const [cycle, setCycle] = useState(null);
  const [onlyNotRecertified, setOnlyNotRecertified] = useState(true);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [showReminder, setShowReminder] = useState(false);
  const [reminderMsg, setReminderMsg] = useState({});
  const [reminderAudio, setReminderAudio] = useState(null);
  const [settings, setSettings] = useState(null);
  const { createOrUpdateMessage } = useContext(PresetMessagesContext);

  const loadCycle = useCallback(
    () =>
      getCycle(id)
        .then((data) => {
          setCycle(data);
          setSettings({
            endDate: new Date(data.endDate),
            batchSize: data.batchSize,
          });
        })
        .catch(() => {}),
    [id]
  );

  useEffect(() => {
    setLoading(true);
    loadCycle().finally(() => setLoading(false));
  }, [loadCycle]);

  const run = async (action, successMessage) => {
    setConfirm(null);
    setBusy(true);
    try {
      const result = await action();
      notyf.success(
        typeof successMessage === "function"
          ? successMessage(result)
          : successMessage
      );
      await loadCycle();
    } catch (_err) {
    } finally {
      setBusy(false);
    }
  };

  const handleExport = async () => {
    const data = await exportCycle(id, onlyNotRecertified);
    downloadFile(
      data,
      `recert-${id}-${onlyNotRecertified ? "not-recertified" : "all"}.csv`
    );
  };

  const handleSaveSettings = () =>
    run(
      () =>
        updateSettings(id, {
          endDate: settings.endDate,
          batchSize: Number(settings.batchSize),
        }),
      "Saved"
    );

  const handleSaveReminder = async () => {
    setBusy(true);
    try {
      const round = (cycle.progress.reminders?.length || 0) + 1;
      const saved = await createOrUpdateMessage(
        {
          ...reminderMsg,
          name: `Recertification reminder ${round} ${cycle.locationName} ${new Date().toLocaleDateString()}`.slice(
            0,
            100
          ),
        },
        reminderAudio
      );
      if (!saved?.id) throw new Error("The reminder could not be saved");
      await addReminder(id, saved.id);
      notyf.success(`Reminder ${round} saved. Send it in batches below.`);
      setShowReminder(false);
      setReminderMsg({});
      setReminderAudio(null);
      await loadCycle();
    } catch (err) {
      if (err?.message) notyf.error(err.message);
    }
    setBusy(false);
  };

  if (loading && !cycle) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (!cycle) {
    return <p>This recertification could not be loaded.</p>;
  }

  const p = cycle.progress;
  const isOpen = cycle.statusName === "Open";
  const reminders = p.reminders || [];

  const confirmations = {
    batch: {
      title: `Queue and send the opening message to the next ${Math.min(
        cycle.batchSize,
        p.inviteRemaining
      ).toLocaleString()} families?`,
      okVariant: "primary",
      run: () => run(() => sendBatch(id), (r) => r.message),
    },
    close: {
      title: `Close '${cycle.name}'? Families who never recertified stay inactive.`,
      okVariant: "danger",
      run: () => run(() => closeCycle(id), "Closed"),
    },
  };

  reminders.forEach((r) => {
    confirmations[`rem-${r.id}`] = {
      title: `Send reminder ${r.round} to the next ${Math.min(
        cycle.batchSize,
        r.remaining
      ).toLocaleString()} families who haven't come back?`,
      okVariant: "primary",
      run: () =>
        run(() => sendReminderBatch(id, r.id), (res) => res.message),
    };
  });

  const active = confirm ? confirmations[confirm] : null;

  return (
    <>
      <Popconfirm
        show={!!active}
        title={active?.title}
        okVariant={active?.okVariant}
        okText="Yes, do it"
        onOk={() => active.run()}
        onCancel={() => setConfirm(null)}
      />

      <Modal
        show={showReminder}
        onHide={() => setShowReminder(false)}
        size="lg"
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Write reminder {(reminders.length || 0) + 1}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted">
            This goes only to families who still haven&rsquo;t recertified
            &mdash; {p.stillMissing.toLocaleString()} right now &mdash; in
            batches you send yourself.
          </p>
          <MessageFields
            idPrefix={`recert-reminder-${reminders.length + 1}`}
            value={reminderMsg}
            onChange={setReminderMsg}
            audioFile={reminderAudio}
            onAudioChange={setReminderAudio}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowReminder(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={
              busy || !(reminderMsg.message || reminderMsg.say || reminderAudio)
            }
            onClick={handleSaveReminder}
          >
            {busy && <Spinner animation="border" size="sm" className="me-2" />}
            Save reminder
          </Button>
        </Modal.Footer>
      </Modal>

      <Link
        to="/recertification"
        className="d-inline-flex align-items-center mb-3"
      >
        <ArrowLeft size={16} className="me-1" /> All recertifications
      </Link>

      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start flex-wrap">
            <div>
              <h3 className="mb-1">
                {cycle.name}{" "}
                <Badge bg={isOpen ? "success" : "dark"}>
                  {cycle.statusName}
                </Badge>
                {cycle.isTest && (
                  <Badge bg="warning" text="dark" className="ms-2">
                    Test run
                  </Badge>
                )}
              </h3>
              <div className="text-muted">
                {cycle.locationName} &middot; started{" "}
                {formatDay(cycle.startedOn)} &middot; families can recertify
                until {formatDay(cycle.endDate)}
                {isOpen && cycle.daysRemaining != null && (
                  <> &middot; {cycle.daysRemaining} days left</>
                )}
              </div>
              {cycle.excludedExceptions && (
                <div className="text-muted small mt-1">
                  Held back: {cycle.excludedExceptions}
                </div>
              )}
            </div>
            <div className="mt-2 d-flex align-items-center">
              <Button
                variant="light"
                className="me-2"
                onClick={loadCycle}
                disabled={busy}
                title="Refresh the numbers"
              >
                <RefreshCw size={16} />
              </Button>
              {isOpen && (
                <Button
                  variant="outline-danger"
                  disabled={busy}
                  onClick={() => setConfirm("close")}
                >
                  Close
                </Button>
              )}
            </div>
          </div>
        </Card.Body>
      </Card>

      <Row>
        <Stat
          label="Families in this drive"
          value={p.familiesInScope.toLocaleString()}
          hint={`${p.familiesActiveAtStart.toLocaleString()} were active before`}
        />
        <Stat
          label="Opening message sent"
          value={p.inviteSent.toLocaleString()}
          hint={
            p.inviteQueued > p.inviteSent
              ? `${(p.inviteQueued - p.inviteSent).toLocaleString()} still sending`
              : "nothing waiting"
          }
        />
        <Stat
          label="Back on the list"
          value={p.reactivated.toLocaleString()}
          variant="success"
          hint={`${percentOf(p.reactivated, p.familiesInScope)}% of the drive`}
        />
        <Stat
          label="Still missing"
          value={p.stillMissing.toLocaleString()}
          hint={`${reminders.length} reminder${
            reminders.length === 1 ? "" : "s"
          } written`}
        />
      </Row>

      {isOpen && (
        <Card className="mb-3">
          <Card.Body>
            <h5 className="mb-3">Sending</h5>

            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <strong>Opening message</strong>
                <span className="text-muted small">
                  {p.inviteQueued.toLocaleString()} of{" "}
                  {p.familiesInScope.toLocaleString()} queued &middot;{" "}
                  {p.inviteSent.toLocaleString()} actually sent
                </span>
              </div>
              <SendProgress
                queued={p.inviteQueued}
                sent={p.inviteSent}
                total={p.familiesInScope}
              />
              {p.inviteRemaining > 0 ? (
                <>
                  <Button
                    variant="primary"
                    disabled={busy}
                    onClick={() => setConfirm("batch")}
                  >
                    Send next batch (
                    {Math.min(cycle.batchSize, p.inviteRemaining).toLocaleString()}
                    )
                  </Button>
                  <span className="text-muted small ms-3">
                    {p.inviteRemaining.toLocaleString()} still to queue
                  </span>
                </>
              ) : (
                <span className="text-success">
                  Everyone has been queued for the opening message.
                </span>
              )}
            </div>

            <hr />

            <div className="d-flex justify-content-between align-items-center mb-2">
              <strong>Reminders</strong>
              <Button
                variant={reminders.length ? "light" : "primary"}
                size="sm"
                disabled={busy}
                onClick={() => setShowReminder(true)}
              >
                {reminders.length ? "Write another reminder" : "Write a reminder"}
              </Button>
            </div>

            {reminders.length === 0 && (
              <p className="text-muted mb-0">
                Nothing written yet. A reminder only goes to families who still
                haven&rsquo;t come back &mdash;{" "}
                {p.stillMissing.toLocaleString()} right now. You can send as
                many rounds as the drive needs.
              </p>
            )}

            {reminders.map((r) => (
              <div key={r.id} className="mb-3 pb-3 border-bottom">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span>
                    <strong>Reminder {r.round}</strong>{" "}
                    <span className="text-muted small">{r.messageName}</span>
                  </span>
                  <span className="text-muted small">
                    {r.queued.toLocaleString()} queued &middot;{" "}
                    {r.sent.toLocaleString()} actually sent
                  </span>
                </div>
                <SendProgress
                  queued={r.queued}
                  sent={r.sent}
                  total={r.queued + r.remaining}
                />
                {r.remaining > 0 ? (
                  <>
                    <Button
                      variant="primary"
                      size="sm"
                      disabled={busy}
                      onClick={() => setConfirm(`rem-${r.id}`)}
                    >
                      Send next batch (
                      {Math.min(cycle.batchSize, r.remaining).toLocaleString()})
                    </Button>
                    <span className="text-muted small ms-3">
                      {r.remaining.toLocaleString()} still to queue
                    </span>
                  </>
                ) : (
                  <span className="text-success small">
                    Everyone still missing has had this reminder.
                  </span>
                )}
              </div>
            ))}
          </Card.Body>
        </Card>
      )}

      {isOpen && settings && (
        <Card className="mb-3">
          <Card.Body>
            <h5 className="mb-3">Settings</h5>
            <Row className="align-items-end">
              <Col md={4} className="mb-2">
                <Form.Label>Families can recertify until</Form.Label>
                <DatePicker
                  className="form-control"
                  selected={settings.endDate}
                  minDate={new Date()}
                  onChange={(value) =>
                    setSettings({ ...settings, endDate: value })
                  }
                />
              </Col>
              <Col md={3} className="mb-2">
                <Form.Label>Families per batch</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  step={100}
                  value={settings.batchSize}
                  onChange={(e) =>
                    setSettings({ ...settings, batchSize: e.target.value })
                  }
                />
              </Col>
              <Col md={3} className="mb-2">
                <Button
                  variant="light"
                  disabled={busy}
                  onClick={handleSaveSettings}
                >
                  Save
                </Button>
              </Col>
            </Row>
            <Form.Text muted>
              Changing the end date moves every family&rsquo;s phone deadline
              with it.
            </Form.Text>
          </Card.Body>
        </Card>
      )}

      {!isOpen && (
        <Alert variant="secondary">
          Closed {formatDay(cycle.closedOn)}.{" "}
          {p.familiesInScope - p.reactivated > 0 && (
            <>
              <strong>
                {(p.familiesInScope - p.reactivated).toLocaleString()}
              </strong>{" "}
              families never came back and are still inactive. Export the list
              to follow up.
            </>
          )}
        </Alert>
      )}

      <Card>
        <Card.Body className="d-flex justify-content-between align-items-center flex-wrap">
          <div>
            <strong>Export the families</strong>
            <div className="text-muted small">
              Names, phone numbers, addresses, what was sent to each, and who
              came back. Too many to show on screen, so download it.
            </div>
          </div>
          <div className="d-flex align-items-center mt-2">
            <Form.Check
              type="switch"
              id="recert-only-missing"
              className="me-3 text-nowrap"
              label="Only families who have not recertified"
              checked={onlyNotRecertified}
              onChange={(e) => setOnlyNotRecertified(e.target.checked)}
            />
            <Button
              variant="primary"
              onClick={handleExport}
              disabled={!p.familiesInScope}
            >
              <Download size={16} className="me-1" /> Export CSV
            </Button>
          </div>
        </Card.Body>
      </Card>
    </>
  );
}

export default RecertificationDetail;
